import { DecodedWidgetConfig } from "~/pages/GoogleDashboardPage/types/DecodedWidgetConfig.type";

import {
  GoogleAnalyticsPropertyReport,
  GoogleSearchConsoleReport,
} from "../../../types/RenderableWidget.type";

type InputDataRow = NonNullable<GoogleSearchConsoleReport["rows"]>[0];

type InputDataMetricKey = Extract<
  keyof InputDataRow,
  "clicks" | "impressions" | "ctr" | "position"
>;

type MetricHeader = {
  name: string;
  type: "TYPE_INTEGER" | "TYPE_PERCENT" | "TYPE_FLOAT";
};

const AVAILABLE_METRIC_FIELDS = Object.entries({
  clicks: { name: "clicks", type: "TYPE_INTEGER" },
  impressions: { name: "impressions", type: "TYPE_INTEGER" },
  ctr: { name: "ctr", type: "TYPE_PERCENT" },
  position: { name: "position", type: "TYPE_FLOAT" },
} satisfies Record<InputDataMetricKey, MetricHeader>).map(([key, value]) => {
  return {
    key: key as InputDataMetricKey,
    header: value,
  };
});

export const googleSearchConsoleDataAdapter = (
  data: GoogleSearchConsoleReport,
  query: Extract<DecodedWidgetConfig, { method: "search_analytics" }>["query"],
): GoogleAnalyticsPropertyReport => {
  const dimension_headers = (query.dimensions ?? []).map((dimension) => ({
    name: dimension,
  }));

  const metric_headers = AVAILABLE_METRIC_FIELDS.map(({ header }) => header);

  return {
    kind: "analyticsData#runReport",
    dimension_headers,
    metric_headers,
    row_count: data.rows?.length,
    rows: data.rows?.map((row) => {
      const keysCount = row.keys?.length ?? 0;

      if (keysCount != dimension_headers.length) {
        throw new Error(
          `Invalid data for a Google Search Console widget encountered.\n` +
            `\tExpected dimensions: ${JSON.stringify(query.dimensions)}\n` +
            `\tGot keys: ${JSON.stringify(row.keys)}`,
        );
      }

      return {
        dimension_values: row.keys?.map((key) => ({ value: key })),
        metric_values: AVAILABLE_METRIC_FIELDS.map(({ key }) => ({
          value: row[key].toString(),
        })),
      };
    }),
  };
};
